/**
 * Usage Widget Component - Displays current usage vs limits with beautiful progress indicators
 */
import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  LinearProgress,
  Grid,
  Chip,
  useTheme
} from '@mui/material';
import {
  People as UsersIcon,
  LocationOn as LocationIcon,
  Inventory as ProductIcon,
  Receipt as OrderIcon,
  Storage as StorageIcon,
  Info as InfoIcon,
  Circle as CircleIcon
} from '@mui/icons-material';

/**
 * Get icon for metric type
 */
const getMetricIcon = (metricType) => {
  const iconProps = { sx: { fontSize: 24 } }; // Slightly larger icons

  switch (metricType) {
    case 'USERS':
      return <UsersIcon {...iconProps} color="primary" />;
    case 'LOCATIONS':
      return <LocationIcon {...iconProps} color="secondary" />;
    case 'PRODUCTS':
      return <ProductIcon {...iconProps} color="success" />;
    case 'ORDERS':
      return <OrderIcon {...iconProps} color="warning" />;
    case 'STORAGE_MB':
      return <StorageIcon {...iconProps} color="info" />;
    default:
      return <InfoIcon {...iconProps} color="action" />;
  }
};

/**
 * Get display name for metric type
 */
const getMetricDisplayName = (metricType) => {
  switch (metricType) {
    case 'USERS':
      return 'Users';
    case 'LOCATIONS':
      return 'Locations';
    case 'PRODUCTS':
      return 'Products';
    case 'ORDERS':
      return 'Monthly Orders';
    case 'STORAGE_MB':
      return 'Storage';
    default:
      return metricType;
  }
};

/**
 * Format usage value for display
 */
const formatUsageValue = (value, metricType) => {
  if (metricType === 'STORAGE_MB') {
    if (value >= 1024) {
      return `${(value / 1024).toFixed(1)} GB`;
    }
    return `${value} MB`;
  }
  return value?.toLocaleString() || '0';
};

/**
 * Individual Usage Metric Component
 * planLimit: from plan only; addonBonus: extra from add-ons. Plan is consumed first, then add-on.
 */
const UsageMetric = ({ metricType, current, limit, percentage, planLimit, addonBonus = 0, compact = false }) => {
  const theme = useTheme();
  const isUnlimited = limit === -1;
  const currentNum = Number(current) || 0;
  const limitNum = Number(limit) || 0;
  const totalRemaining = isUnlimited ? null : Math.max(0, limitNum - currentNum);
  // Remaining from plan (plan capacity used first): how many of the plan slots are left
  const remainingFromPlan = planLimit != null ? Math.max(0, (Number(planLimit) || 0) - currentNum) : null;
  const hasAddon = (Number(addonBonus) || 0) > 0;

  // Determine progress color based on percentage
  let progressColor = 'primary';
  if (!isUnlimited) {
    if (percentage >= 90) progressColor = 'error';
    else if (percentage >= 75) progressColor = 'warning';
    else progressColor = 'success';
  }

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          boxShadow: '0 4px 12px rgba(0,0,0,0.05)',
          borderColor: 'primary.light',
          transform: 'translateY(-2px)'
        }
      }}
    >
      <CardContent sx={{ p: compact ? 2 : 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                p: 1,
                borderRadius: '50%',
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.04)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              {getMetricIcon(metricType)}
            </Box>
            <Typography variant={compact ? "body1" : "h6"} fontWeight={600} fontFamily="Roboto">
              {getMetricDisplayName(metricType)}
            </Typography>
          </Box>
          {!isUnlimited && (
            <Chip
              label={`${percentage.toFixed(0)}%`}
              size="small"
              color={progressColor}
              variant="outlined"
              sx={{ fontWeight: 'bold', height: 24 }}
            />
          )}
        </Box>

        <Box mb={3}>
          <Box display="flex" alignItems="baseline" gap={0.5}>
            <Typography variant="h4" component="div" fontWeight={700} color="text.primary" fontFamily="Roboto">
              {formatUsageValue(current, metricType)}
            </Typography>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              / {isUnlimited ? 'Unlimited' : formatUsageValue(limit, metricType)}
            </Typography>
          </Box>

          {!isUnlimited && totalRemaining !== null && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.875rem' }}>
              {formatUsageValue(totalRemaining, metricType)} remaining
            </Typography>
          )}

          {/* Breakdown: remaining from plan, then add-on (+2) so it's clear: initial 5 + newly added 2 = 7 */}
          {!isUnlimited && (remainingFromPlan !== null || hasAddon) && (
            <Box sx={{ mt: 1.5, pl: 1, borderLeft: '3px solid', borderColor: 'divider' }}>
              {remainingFromPlan !== null && (
                <Typography variant="caption" color="text.secondary" display="block">
                  Plan: {formatUsageValue(remainingFromPlan, metricType)} left
                </Typography>
              )}
              {hasAddon && (
                <Typography variant="caption" color="primary" fontWeight={600} display="block">
                  + Add-on: {formatUsageValue(addonBonus, metricType)}
                </Typography>
              )}
            </Box>
          )}
        </Box>

        {!isUnlimited && (
          <Box>
            <LinearProgress
              variant="determinate"
              value={Math.min(percentage, 100)}
              color={progressColor}
              sx={{
                height: 10,
                borderRadius: 5,
                backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 5,
                }
              }}
            />
          </Box>
        )}

        {isUnlimited && (
          <Box sx={{ mt: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircleIcon sx={{ fontSize: 12, color: 'success.main' }} />
            <Typography variant="body2" color="success.main" fontWeight={500}>
              No limits applied
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

/**
 * Main Usage Widget Component
 */
const UsageWidget = ({
  usageData,
  loading = false,
  compact = false,
  showTitle = true,
  onUpgradeClick
}) => {
  if (loading) {
    return (
      <Card elevation={0} variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom fontFamily="Roboto">
            Loading usage data...
          </Typography>
          <LinearProgress sx={{ borderRadius: 2, height: 6 }} />
        </CardContent>
      </Card>
    );
  }

  if (!usageData || !usageData.metrics) {
    return (
      <Card elevation={0} variant="outlined">
        <CardContent>
          <Typography variant="h6" color="text.secondary" fontFamily="Roboto">
            No usage data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const { metrics } = usageData;
  const metricsArray = Object.entries(metrics).map(([type, data]) => ({
    metricType: type,
    ...data
  }));

  // Sort metrics by usage percentage (highest first)
  metricsArray.sort((a, b) => (b.percentage || 0) - (a.percentage || 0));

  return (
    <Box>
      {showTitle && (
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Typography variant="h5" fontWeight={700} fontFamily="Roboto">
            Usage Overview
          </Typography>
        </Box>
      )}

      {/* Usage Metrics Grid */}
      <Grid container spacing={compact ? 2 : 3}>
        {metricsArray.map((metric) => (
          <Grid
            item
            xs={12}
            sm={compact ? 6 : 6}
            md={compact ? 4 : 4}
            lg={3}
            key={metric.metricType}
          >
            <UsageMetric {...metric} compact={compact} />
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

export default UsageWidget;
